import { z } from 'zod';
import type { IdentifierValidationKind } from '../constants/identifierTypes';
import { firstError } from '@/utils/formSchemas';

export interface IdentifierValidationResult {
  normalizedValue: string;
  error?: string;
}

const CID_REGEX = /^[A-Fa-f0-9]{16}$/;
const DOMAIN_REGEX = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const GAMERTAG_REGEX = /^[A-Za-z0-9 ]{1,15}$/;
const GUID_REGEX = /^(?:[0-9A-Fa-f]{32}|[0-9A-Fa-f]{8}[- ][0-9A-Fa-f]{4}[- ][0-9A-Fa-f]{4}[- ][0-9A-Fa-f]{4}[- ][0-9A-Fa-f]{12})$/;
const IPV4_REGEX = /^(25[0-5]|2[0-4]\d|1?\d\d?|0)(\.(25[0-5]|2[0-4]\d|1?\d\d?|0)){3}$/;
const IPV6_REGEX = /^((?:[A-F0-9]{1,4}:){7}[A-F0-9]{1,4}|(?:[A-F0-9]{1,4}:){1,7}:|(?:[A-F0-9]{1,4}:){1,6}:[A-F0-9]{1,4}|(?:[A-F0-9]{1,4}:){1,5}(?::[A-F0-9]{1,4}){1,2}|(?:[A-F0-9]{1,4}:){1,4}(?::[A-F0-9]{1,4}){1,3}|(?:[A-F0-9]{1,4}:){1,3}(?::[A-F0-9]{1,4}){1,4}|(?:[A-F0-9]{1,4}:){1,2}(?::[A-F0-9]{1,4}){1,5}|[A-F0-9]{1,4}:(?::[A-F0-9]{1,4}){1,6}|:(?:(?::[A-F0-9]{1,4}){1,7}|:)|fe80:(?::[A-F0-9]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(?:ffff(?::0{1,4}){0,1}:){0,1}(?:25[0-5]|2[0-4]\d|1?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|1?\d\d?)){3}|(?:[A-F0-9]{1,4}:){1,4}:(?:25[0-5]|2[0-4]\d|1?\d\d?)(?:\.(?:25[0-5]|2[0-4]\d|1?\d\d?)){3})$/i;
const MICROSOFT_FORMS_URL_REGEX = /^https:\/\/forms\.(office\.com|microsoft\.com|cloud\.microsoft)\//i;
const MRN_REGEX = /^[A-Za-z0-9]{1,50}$/;
const NCMEC_REPORT_ID_REGEX = /^\d{1,20}$/;
const PHONE_ALLOWED_LENGTH = { min: 7, max: 15 };
const PHONE_LENGTH_MESSAGE = `Phone number must be between ${PHONE_ALLOWED_LENGTH.min} and ${PHONE_ALLOWED_LENGTH.max} digits after formatting.`;
const PUID_REGEX = /^(?:[0-9A-Fa-f]{16}|[1-9][0-9]{14,19})$/;
const SHAREPOINT_URL_REGEX = /^https:\/\/[a-zA-Z0-9-]+\.sharepoint\.com/i;
const SKYPE_ID_REGEX = /^[a-zA-Z][a-zA-Z0-9.,:_-]{5,31}$/;
const XBOX_5X5_TOKEN_REGEX = /^[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}-[A-Z0-9]{5}$/i;

const URL_MAX_LENGTH = 300;

const isHttpUrl = (value: string): boolean => {
  try {
    const { protocol } = new URL(value);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/** Formats a validated GUID, with or without separators, in the dashed 8-4-4-4-12 form. */
const formatGuid = (value: string): string => {
  const hex = value.replace(/[- ]/g, '');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

/**
 * How each identifier kind is cleaned up and then checked. The schema's output is the value
 * that gets stored; a value that fails keeps its cleaned-up form so the field still shows it.
 */
interface IdentifierRule {
  normalize: (value: string) => string;
  schema: z.ZodType<string, string>;
}

const trim = (value: string) => value.trim();
const trimLower = (value: string) => value.trim().toLowerCase();
const trimUpper = (value: string) => value.trim().toUpperCase();

const IDENTIFIER_RULES: Record<IdentifierValidationKind, IdentifierRule> = {
  cid: {
    normalize: trimUpper,
    schema: z.string().regex(CID_REGEX, 'Enter a properly formatted CID identifier.'),
  },
  domain: {
    normalize: trimLower,
    schema: z.string().regex(DOMAIN_REGEX, 'Enter a valid domain name (e.g., example.com).'),
  },
  email: {
    normalize: trimLower,
    schema: z.string().regex(EMAIL_REGEX, 'Enter a valid email address.'),
  },
  formsUrl: {
    normalize: trim,
    schema: z
      .string()
      .regex(MICROSOFT_FORMS_URL_REGEX, 'Enter a valid Microsoft Forms URL (e.g., https://forms.office.com/...).'),
  },
  gamertag: {
    normalize: trim,
    schema: z
      .string()
      .regex(GAMERTAG_REGEX, 'Gamertag must be 1-15 characters and can include letters, numbers, and spaces.'),
  },
  guid: {
    normalize: trimLower,
    schema: z
      .string()
      .regex(GUID_REGEX, 'Enter a valid GUID (e.g., 12345678-1234-1234-1234-123456789abc).')
      .transform(formatGuid),
  },
  ip: {
    normalize: trim,
    schema: z
      .string()
      .refine((value) => IPV4_REGEX.test(value) || IPV6_REGEX.test(value), {
        error: 'Enter a valid IPv4 or IPv6 address.',
      }),
  },
  mrn: {
    normalize: trim,
    schema: z.string().regex(MRN_REGEX, 'MRN must be 1-50 alphanumeric characters.'),
  },
  ncmecReportId: {
    normalize: trim,
    schema: z.string().regex(NCMEC_REPORT_ID_REGEX, 'NCMEC Report ID must be a numeric value.'),
  },
  phone: {
    normalize: (value) => value.replace(/[^0-9]/g, ''),
    schema: z
      .string()
      .min(PHONE_ALLOWED_LENGTH.min, PHONE_LENGTH_MESSAGE)
      .max(PHONE_ALLOWED_LENGTH.max, PHONE_LENGTH_MESSAGE),
  },
  puid: {
    normalize: trimUpper,
    schema: z
      .string()
      .regex(PUID_REGEX, 'PUID must be 16 hexadecimal characters or a 15-20 digit decimal number.'),
  },
  sharePointUrl: {
    normalize: trim,
    schema: z
      .string()
      .regex(SHAREPOINT_URL_REGEX, 'Enter a valid SharePoint URL (e.g., https://contoso.sharepoint.com/...).'),
  },
  skypeId: {
    normalize: trim,
    schema: z
      .string()
      .regex(
        SKYPE_ID_REGEX,
        'Skype ID must be 6-32 characters, start with a letter, and contain only letters, numbers, periods, commas, colons, underscores, or hyphens.',
      ),
  },
  url: {
    normalize: trim,
    // The scheme is checked before the length, and a bad scheme stops there.
    schema: z
      .string()
      .refine(isHttpUrl, { error: 'Enter a valid URL starting with http:// or https://.', abort: true })
      .max(URL_MAX_LENGTH, `URL must not exceed ${URL_MAX_LENGTH} characters.`),
  },
  xbox5x5Token: {
    normalize: trimUpper,
    schema: z
      .string()
      .regex(XBOX_5X5_TOKEN_REGEX, 'XBOX 5X5 Token must be in format XXXXX-XXXXX-XXXXX-XXXXX-XXXXX.'),
  },
};

export const validateIdentifierValue = (
  validationKind: IdentifierValidationKind | undefined,
  value: string,
): IdentifierValidationResult => {
  const rule = validationKind ? IDENTIFIER_RULES[validationKind] : undefined;
  if (!rule) {
    return { normalizedValue: value.trim() };
  }

  const normalized = rule.normalize(value);
  const result = rule.schema.safeParse(normalized);
  return result.success
    ? { normalizedValue: result.data }
    : { normalizedValue: normalized, error: firstError(result.error) };
};
